import { CompanyProductSetQuality } from '../../internal/product-resolver/types';
import { SpecialUserGroupId, UserGroupPlain } from '../user-groups/types';
import { User } from '../users/types';

export interface Access {
  objectId: AccessObjectId;
  objectType: AccessObjectType;
  objectName?: string;
  accessLevel: AccessLevel;
  scenarioId?: number; // To be removed in CRHv2
  subjectId: string; // In numeric form for user and user group accesses
  subjectType: AccessSubjectType;
  subjectUser?: AccessSubjectUser;
  subjectUserGroup?: AccessSubjectUserGroup;
  updatedBy?: number;
  updatedByUser?: User;
  updatedAt?: string;
  itemCount?: number;
  itemsWithoutReadAccess?: MissingAccess[];
}

// Newer, stricter type that makes a distinction between weightable entity accesses
interface AccessBase {
  accessLevel: AccessLevel;
  objectId: AccessObjectId;
  objectName: string;
  subjectId: AccessSubjectId;
  subjectType: AccessSubjectType;
  subjectName: string;
  updatedAt: string;
}

export type NonWeightedEntityAccess = AccessBase & {
  objectType: Exclude<AccessObjectType, AccessObjectType.PORTFOLIO | AccessObjectType.COMPANY_GROUP>;
};
export type WeightedEntityAccess = AccessBase & {
  objectType: AccessObjectType.PORTFOLIO | AccessObjectType.COMPANY_GROUP;
  itemCount?: number; // TODO ML: Maybe remove, only used in get-accesses-of-object
  itemsWithoutReadAccess?: MissingAccess[]; // TODO ML: Maybe remove, only used in get-accesses-of-object
};
export type EntityAccess = WeightedEntityAccess | NonWeightedEntityAccess;

export function isWeightedEntityAccess(input: EntityAccess): input is WeightedEntityAccess {
  return input.objectType === AccessObjectType.PORTFOLIO || input.objectType === AccessObjectType.COMPANY_GROUP;
}
export function isWeightedEntityAccessArray(input: EntityAccess[]): input is WeightedEntityAccess[] {
  return input.every(access => isWeightedEntityAccess(access));
}
export function isNonWeightedEntityAccess(input: EntityAccess): input is NonWeightedEntityAccess {
  return input.objectType !== AccessObjectType.PORTFOLIO && input.objectType !== AccessObjectType.COMPANY_GROUP;
}
export function isNonWeightedEntityAccessArray(input: EntityAccess[]): input is NonWeightedEntityAccess[] {
  return input.every(access => isNonWeightedEntityAccess(access));
}

export type AccessObjectId = string;
export type AccessSubjectId = string;

export interface AccessObject {
  objectId: AccessObjectId;
  objectType: AccessObjectType;
}

// NOTE: keep in sync with Postgres functions
export enum AccessSpace {
  SHARED = 'SHARED', // At least findable by all, read accesses freely configurable
  PRIVATE = 'PRIVATE', // Only visible / owned by a single customer, confidential data
  SEMIPRIVATE = 'SEMIPRIVATE', // Not findable by public, access can be granted to multiple customers
  PROMISED_PUBLIC = 'PROMISED_PUBLIC', // Must be readable by all
}

export const allAccessSpaces = Object.values(AccessSpace);

// NOTE: keep in sync with Postgres functions
export enum AccessObjectType {
  SOURCE = 'src',
  COMPANY = 'c',
  COMPANY_GROUP = 'g',
  PORTFOLIO = 'p',
  VARIANT_SCENARIO = 'vs',
  CSRD_RESULT_ENTITY = 'csrd', // companyId, groupId or portfolioId
}

export interface AccessSubject {
  subjectId: string;
  subjectType: AccessSubjectType;
  subjectUser?: AccessSubjectUser;
  subjectUserGroup?: AccessSubjectUserGroup;
}

export type AccessSubjectUserGroup = {
  id: number;
  name: string;
  description?: string;
  parents?: AccessSubjectUserGroup[];
};

export type AccessSubjectUser = {
  id: number;
  email: string;
  username: string;
  groups: UserGroupPlain[];
};

// NOTE: keep in sync with Postgres functions
// NOTE: Ordered from high to low!
export enum AccessLevel {
  OWNER = 'o',
  EDIT = 'e',
  READ_ONLY = 'r',
  READ_WITHOUT_WEIGHTS = 'rww',
  READ_OUTPUT = 'ro',
  READ_TEASER = 'rt',
  SEARCH_ONLY = 's',
}

// Ordered from high to low
export const allAccessLevels = Object.values(AccessLevel);
export const readAccessLevels = allAccessLevels.filter(accessLevel => isEnoughAccessToReadOutput(accessLevel));

export function accessLevelHierarchy(accessLevel: AccessLevel) {
  const index = allAccessLevels.indexOf(accessLevel);
  return index === -1 ? 99 : index + 1; // [1, 7], [99]
}

export type AccessesForSingleSubject = {
  [key in AccessObjectType]: AccessListHash | undefined;
};

export interface AccessListHash {
  [key: string]: AccessLevel;
}

export interface AccessIdList {
  objectType: AccessObjectType;
  ids: string[];
}

export function getAllAccessObjectTypes(): AccessObjectType[] {
  return [
    AccessObjectType.SOURCE,
    AccessObjectType.COMPANY,
    AccessObjectType.COMPANY_GROUP,
    AccessObjectType.PORTFOLIO,
    AccessObjectType.VARIANT_SCENARIO,
    AccessObjectType.CSRD_RESULT_ENTITY,
  ];
}

export function isAccessObjectType(type: string): type is AccessObjectType {
  return getAllAccessObjectTypes().indexOf(type as AccessObjectType) > -1;
}

export function getAllAccessSubjectTypes(): AccessSubjectType[] {
  return [AccessSubjectType.USER, AccessSubjectType.USER_GROUP];
}

export function isAccessSubjectType(type: string): type is AccessSubjectType {
  return getAllAccessSubjectTypes().indexOf(type as AccessSubjectType) > -1;
}

export function subjectTypeHierarchy(subjectType: AccessSubjectType) {
  switch (subjectType) {
    case AccessSubjectType.USER_GROUP:
      return 0;
    case AccessSubjectType.USER:
      return 1;
    default:
      return 99;
  }
}

// NOTE: keep in sync with Postgres functions
export enum AccessSubjectType {
  USER = 'u',
  USER_GROUP = 'g',
  SESSION = 's',
}

export function isAccessLevel(type: string): type is AccessLevel {
  return allAccessLevels.indexOf(type as AccessLevel) > -1;
}

export function isAccessSpace(type: string): type is AccessSpace {
  return allAccessSpaces.indexOf(type as AccessSpace) > -1;
}

export function getStrongerAccessLevels(minAccessLevel: AccessLevel) {
  return allAccessLevels.filter(accessLevel => isEnoughAccess(accessLevel, minAccessLevel));
}

export function getAllowedAccessLevels(userGroupIds: number[], objectType: AccessObjectType): AccessLevel[] {
  if (userGroupIds.includes(SpecialUserGroupId.TEAM)) {
    const showReadWithoutWeights = objectType === AccessObjectType.COMPANY_GROUP;
    const showReadTeaser = objectType === AccessObjectType.COMPANY;
    return allAccessLevels.filter(a => {
      if (!showReadWithoutWeights && a === AccessLevel.READ_WITHOUT_WEIGHTS) {
        return false;
      }
      if (!showReadTeaser && a === AccessLevel.READ_TEASER) {
        return false;
      }
      return true;
    });
  }

  return [AccessLevel.OWNER, AccessLevel.EDIT, AccessLevel.READ_ONLY];
}

export function isEnoughAccessToSearch(accessLevel?: AccessLevel): accessLevel is AccessLevel {
  if (!accessLevel) {
    return false;
  }
  return isEnoughAccess(accessLevel, AccessLevel.SEARCH_ONLY);
}

export function isEnoughAccessToReadWithWeights(accessLevel?: AccessLevel | null) {
  if (!accessLevel) {
    return false;
  }
  return isEnoughAccess(accessLevel, AccessLevel.READ_ONLY);
}

export function isEnoughAccessToReadOutput(accessLevel?: AccessLevel | null) {
  if (!accessLevel) {
    return false;
  }
  return isEnoughAccess(accessLevel, AccessLevel.READ_OUTPUT);
}

/**
 * This function checks the entity level access rights and also accepts ability to read without weights
 */
export function isEnoughAccessToReadWithoutWeights(accessLevel?: AccessLevel | null) {
  if (!accessLevel) {
    return false;
  }
  return isEnoughAccess(accessLevel, AccessLevel.READ_WITHOUT_WEIGHTS);
}

export function isEnoughAccessToTeaserRead(accessLevel?: AccessLevel | null) {
  if (!accessLevel) {
    return false;
  }
  return isEnoughAccess(accessLevel, AccessLevel.READ_TEASER);
}

export function isEnoughAccessToEdit(accessLevel?: AccessLevel | null) {
  if (!accessLevel) {
    return false;
  }
  return isEnoughAccess(accessLevel, AccessLevel.EDIT);
}

export function isEnoughAccessToOwn(accessLevel?: AccessLevel) {
  if (!accessLevel) {
    return false;
  }
  return isEnoughAccess(accessLevel, AccessLevel.OWNER);
}

export function isEnoughAccess(accessOfUser: AccessLevel | undefined, requiredAccess: AccessLevel) {
  if (accessOfUser === undefined) {
    return false;
  }
  return accessLevelHierarchy(accessOfUser) <= accessLevelHierarchy(requiredAccess);
}

export function accessLevelsEnoughTo(accessLevel: AccessLevel) {
  return allAccessLevels.filter(al => isEnoughAccess(al, accessLevel));
}

export interface MissingAccess {
  type: AccessObjectType.COMPANY_GROUP | AccessObjectType.COMPANY;
  id: string;
  name: string;
  productSetQuality?: CompanyProductSetQuality;
}

export interface MissingAccessOptions {
  includeProductSetQuality?: true;
}

export enum VisibilityCategory {
  PUBLIC = 'public',
  CLIENT = 'client',
  TEAM = 'team',
}
